import React from "react";
import parse, {
  HTMLReactParserOptions,
  domToReact,
  Element,
} from "html-react-parser";
import { twMerge } from "tailwind-merge";

const RichTextRenderer = ({
  value,
  className,
  style,
}: {
  value: string;
  className?: string;
  style?: React.CSSProperties;
}) => {
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      // Style and Render <h1> tags
      if ((domNode as Element).name === "h1") {
        return (
          <h1
            className={twMerge(
              "font-heading text-5xl tracking-[3.84px] text-white lg:text-7xl lg:tracking-[2.88px]",
              className
            )}
            style={style}
          >
            {domToReact((domNode as Element).children)}
          </h1>
        );
      }
      // Style and Render <h2> tags
      if ((domNode as Element).name === "h2") {
        return (
          <h2
            className={twMerge(
              "font-body text-[32px] font-bold leading-[1.33] tracking-[0.64px] text-midnight lg:text-5xl lg:tracking-[0.96px] [&_a]:underline",
              className
            )}
            style={style}
          >
            {domToReact((domNode as Element).children)}
          </h2>
        );
      }
      // Style and Render <h3> tags
      if ((domNode as Element).name === "h3") {
        return (
          <h3
            className={twMerge(
              "font-body text-2xl tracking-[1.44px] text-midnight lg:text-[32px] lg:tracking-[0.96px]",
              className
            )}
            style={style}
          >
            {domToReact((domNode as Element).children)}
          </h3>
        );
      }
      // Style and Render <h4> tags
      if ((domNode as Element).name === "h4") {
        return (
          <h4
            className={twMerge("font-body font-bold text-midnight", className)}
            style={style}
          >
            {domToReact((domNode as Element).children)}
          </h4>
        );
      }
      // Style and Render <p> tags
      if ((domNode as Element).name === "p") {
        return (
          <p
            className={twMerge(
              "font-body tracking-[0.16px] text-midnight lg:text-lg [&_a]:underline",
              className
            )}
            style={style}
          >
            {domToReact((domNode as Element).children)}
          </p>
        );
      }
      // Style and Render <ul> tags
      if ((domNode as Element).name === "ul") {
        return (
          <ul
            className={twMerge("list-disc pl-8 font-body", className)}
            style={style}
          >
            {domToReact((domNode as Element).children)}
          </ul>
        );
      }
      // <table> tag
      if ((domNode as Element).name === "table") {
        return (
          <table
            // Sorry... use tailwind docs to learn more about psuedo and child selectors... it will make sense eventually -Spencer
            className={twMerge(
              "mx-auto max-w-[800px] border-separate border-spacing-[3px] font-body [&_td]:justify-center [&_td]:px-2 [&_td]:py-1 md:[&_td]:p-4 [&_td]:[&_tr:not(:first-child)]:bg-white first:[&_tr]:bg-abyss first:[&_tr]:text-white first:[&_td]:[&_tr]:bg-abyss first:[&_td]:first:[&_tr]:bg-primary-dark first:[&_td]:[&_tr]:text-white",
              className
            )}
            style={style}
          >
            {domToReact((domNode as Element).children)}
          </table>
        );
      }
      // Style and Render <figure> tags
      if ((domNode as Element).name === "figure") {
        return (
          <figure
            className={twMerge("inline-block align-middle m-2", className)}
            style={style}
          >
            {domToReact((domNode as Element).children)}
          </figure>
        );
      }
      // Return the default rendering for other HTML elements
      return domNode;
    },
  };

  const parsedContent = parse(value, options);

  return <>{parsedContent}</>;
};

export default RichTextRenderer;
